/**
 * Supabase Storage Service
 *
 * Handles file uploads to Supabase Storage (S3-backed) and generates signed URLs
 * for secure file access. This service is used for uploading audio recordings
 * before sending URLs to n8n for processing.
 */

import { supabase } from './supabase';

export interface SupabaseUploadResult {
  success: boolean;
  filePath?: string;
  signedUrl?: string;
  publicUrl?: string;
  error?: string;
}

export interface SupabaseUploadOptions {
  bucket: string;
  filePath: string;
  file: Blob | File;
  contentType?: string;
  upsert?: boolean;
  expiresIn?: number; // Expiry time for signed URL in seconds (default: 1 hour)
}

/**
 * Supabase Storage Service for handling file uploads and URL generation
 */
export const supabaseStorageService = {
  /**
   * Upload a file to Supabase Storage and generate a signed URL
   *
   * @param options - Upload configuration options
   * @returns Upload result with signed URL
   */
  async uploadFile(
    options: SupabaseUploadOptions
  ): Promise<SupabaseUploadResult> {
    const {
      bucket,
      filePath,
      file,
      contentType = 'audio/wav',
      upsert = false,
      expiresIn = 3600, // Default: 1 hour
    } = options;

    try {
      console.log(`📤 Uploading file to Supabase storage...`, {
        bucket,
        filePath,
        size: file.size,
        type: contentType,
      });

      // Upload the file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          contentType,
          upsert,
          cacheControl: '3600', // Cache for 1 hour
        });

      if (uploadError) {
        console.error('❌ Supabase upload error:', uploadError);
        return {
          success: false,
          error: `Upload failed: ${uploadError.message}`,
        };
      }

      console.log('✅ File uploaded successfully:', uploadData);

      // Use the actual path returned from upload (not the original filePath)
      // Supabase may normalize the path during upload
      const actualPath = uploadData.path;
      console.log('📍 Actual file path from upload:', actualPath);

      // Generate a signed URL for the uploaded file using the actual path
      const { data: signedUrlData, error: signedUrlError } =
        await supabase.storage
          .from(bucket)
          .createSignedUrl(actualPath, expiresIn);

      if (signedUrlError) {
        console.error('❌ Signed URL generation error:', signedUrlError);
        console.error('❌ Attempted path:', actualPath);
        return {
          success: false,
          error: `Signed URL generation failed: ${signedUrlError.message}`,
        };
      }

      console.log('✅ Signed URL generated successfully');

      // Get public URL (not signed, for reference only) using actual path
      const { data: publicUrlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(actualPath);

      return {
        success: true,
        filePath: uploadData.path,
        signedUrl: signedUrlData.signedUrl,
        publicUrl: publicUrlData.publicUrl,
      };
    } catch (error) {
      console.error('❌ Unexpected error during file upload:', error);
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Upload an audio file with auto-generated path
   *
   * @param file - Audio file blob
   * @param config - Recording configuration for generating file path
   * @returns Upload result with signed URL
   */
  async uploadAudioFile(
    file: Blob | File,
    config: {
      locationId: string;
      patientId: string;
      appointmentId: string;
      sectionTag: string;
      scribeJobId: string;
    }
  ): Promise<SupabaseUploadResult> {
    // Generate a unique file path following the structure:
    // exams/{locationId}/{patientId}/{appointmentId}/audio/{filename}
    const timestamp = Date.now();
    const fileName = `${config.sectionTag}-${timestamp}.mpeg`;
    const filePath = `${config.locationId}/${config.patientId}/${config.appointmentId}/audio/${fileName}`;

    return this.uploadFile({
      bucket: 'exams', // Bucket name - should be created in Supabase
      filePath,
      file,
      contentType: 'audio/mpeg',
      upsert: false,
      expiresIn: 86400, // 24 hours (enough time for n8n processing)
    });
  },

  /**
   * Upload a transcript file with auto-generated path
   *
   * @param file - Transcript file blob
   * @param config - Recording configuration for generating file path
   * @returns Upload result with signed URL
   */
  async uploadTranscriptFile(
    file: Blob | File,
    config: {
      locationId: string;
      patientId: string;
      appointmentId: string;
      sectionTag: string;
      scribeJobId: string;
    }
  ): Promise<SupabaseUploadResult> {
    // Generate a unique file path following the structure:
    // exams/{locationId}/{patientId}/{appointmentId}/transcript/{filename}
    const timestamp = Date.now();
    const fileName = `${config.sectionTag}-${timestamp}.txt`;
    const filePath = `${config.locationId}/${config.patientId}/${config.appointmentId}/transcript/${fileName}`;

    return this.uploadFile({
      bucket: 'exams', // Bucket name - should be created in Supabase
      filePath,
      file,
      contentType: 'text/plain',
      upsert: false,
      expiresIn: 86400, // 24 hours (enough time for n8n processing)
    });
  },

  /**
   * Delete a file from Supabase Storage
   *
   * @param bucket - Storage bucket name
   * @param filePath - Path to the file to delete
   * @returns Success status
   */
  async deleteFile(
    bucket: string,
    filePath: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.storage.from(bucket).remove([filePath]);

      if (error) {
        console.error('❌ File deletion error:', error);
        return {
          success: false,
          error: `Deletion failed: ${error.message}`,
        };
      }

      console.log('✅ File deleted successfully:', filePath);
      return { success: true };
    } catch (error) {
      console.error('❌ Unexpected error during file deletion:', error);
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Get a signed URL for an existing file
   *
   * @param bucket - Storage bucket name
   * @param filePath - Path to the file
   * @param expiresIn - Expiry time in seconds (default: 1 hour)
   * @returns Signed URL
   */
  async getSignedUrl(
    bucket: string,
    filePath: string,
    expiresIn: number = 3600
  ): Promise<{ success: boolean; signedUrl?: string; error?: string }> {
    try {
      console.log('🔗 Generating signed URL...', {
        bucket,
        filePath,
        expiresIn,
      });

      // First, verify the file exists
      const { data: listData, error: listError } = await supabase.storage
        .from(bucket)
        .list(filePath.substring(0, filePath.lastIndexOf('/')), {
          limit: 100,
          search: filePath.substring(filePath.lastIndexOf('/') + 1),
        });

      if (listError) {
        console.error('❌ Error checking file existence:', listError);
      } else {
        console.log('📂 Files found in directory:', listData?.length || 0);
      }

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, expiresIn);

      if (error) {
        console.error('❌ Signed URL generation error:', error);
        console.error('❌ Bucket:', bucket);
        console.error('❌ Path:', filePath);
        return {
          success: false,
          error: `Signed URL generation failed: ${error.message}`,
        };
      }

      console.log('✅ Signed URL generated:', data.signedUrl);

      return {
        success: true,
        signedUrl: data.signedUrl,
      };
    } catch (error) {
      console.error('❌ Unexpected error getting signed URL:', error);
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Generate a signed URL from a file key (e.g. "ai-scribe/loc/pat/appt/json/file.json").
   * The first path segment is used as the bucket name, the rest as the file path.
   */
  async getSignedUrlFromFileKey(
    fileKey: string,
    expiresIn: number = 3600
  ): Promise<{ success: boolean; signedUrl?: string; error?: string }> {
    const slashIndex = fileKey.indexOf('/');
    if (slashIndex === -1) {
      return { success: false, error: `Invalid file key: ${fileKey}` };
    }
    const bucket = fileKey.substring(0, slashIndex);
    const filePath = fileKey.substring(slashIndex + 1);
    return this.getSignedUrl(bucket, filePath, expiresIn);
  },

  /**
   * Get public URL for a file (no expiry, but requires public bucket)
   *
   * @param bucket - Storage bucket name
   * @param filePath - Path to the file
   * @returns Public URL
   */
  getPublicUrl(bucket: string, filePath: string): string {
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  },

  /**
   * List files in a storage bucket
   *
   * @param bucket - Storage bucket name
   * @param path - Optional path prefix to filter files
   * @returns List of files
   */
  async listFiles(
    bucket: string,
    path?: string
  ): Promise<{
    success: boolean;
    files?: any[];
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(path || '', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (error) {
        console.error('❌ File listing error:', error);
        return {
          success: false,
          error: `Listing failed: ${error.message}`,
        };
      }

      return {
        success: true,
        files: data,
      };
    } catch (error) {
      console.error('❌ Unexpected error listing files:', error);
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Check if a bucket exists
   *
   * @param bucket - Storage bucket name
   * @returns True if bucket exists
   */
  async bucketExists(bucket: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.storage.getBucket(bucket);
      return !error && !!data;
    } catch (error) {
      console.error('❌ Error checking bucket existence:', error);
      return false;
    }
  },

  /**
   * Test storage permissions - useful for debugging
   *
   * @param bucket - Storage bucket name
   * @returns Test results
   */
  async testStoragePermissions(bucket: string = 'exams'): Promise<{
    canUpload: boolean;
    canRead: boolean;
    canCreateSignedUrl: boolean;
    errors: string[];
  }> {
    const errors: string[] = [];
    let canUpload = false;
    let canRead = false;
    let canCreateSignedUrl = false;

    try {
      // Test upload
      const testBlob = new Blob(['test'], { type: 'text/plain' });
      const testPath = `test/permissions-test-${Date.now()}.txt`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(testPath, testBlob);

      if (uploadError) {
        errors.push(`Upload test failed: ${uploadError.message}`);
      } else {
        canUpload = true;
        console.log('✅ Upload permission: OK');

        // Test read
        const { data: listData, error: listError } = await supabase.storage
          .from(bucket)
          .list('test');

        if (listError) {
          errors.push(`Read test failed: ${listError.message}`);
        } else {
          canRead = true;
          console.log('✅ Read permission: OK');
        }

        // Test signed URL
        const { error: signedUrlError } = await supabase.storage
          .from(bucket)
          .createSignedUrl(testPath, 60);

        if (signedUrlError) {
          errors.push(`Signed URL test failed: ${signedUrlError.message}`);
        } else {
          canCreateSignedUrl = true;
          console.log('✅ Signed URL permission: OK');
        }

        // Cleanup
        await supabase.storage.from(bucket).remove([testPath]);
      }
    } catch (error) {
      errors.push(
        `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }

    return {
      canUpload,
      canRead,
      canCreateSignedUrl,
      errors,
    };
  },
};
